import { useEffect } from "react";
import {
  Driver,
  Maybe,
  OrderingAlgorithmType,
} from "@api/graphql/generated/generated-types";
import { RouteViewMode } from "../ChangeDriverRouteView";
import moment from "moment";

interface UseRouteAlgorithmSelectionParams {
  routeViewMode: RouteViewMode;
  selectedOrderingAlgorithmType: OrderingAlgorithmType | undefined;
  // Parent algorithm type - kept for backward compatibility but currently unused
  parentAlgorithmType: OrderingAlgorithmType | undefined;
  tabOrder: OrderingAlgorithmType[];
  handleOrderingAlgorithmChange: (index: number) => void;
  onClickManual?: () => void;
  setSelectedOrderingAlgorithmType: (type: OrderingAlgorithmType) => void;
  selectedStopDate?: string;
  selectedShipment?: any;
  selectedBaseTime?: moment.Moment;
  driver?: Driver | Maybe<Driver> | undefined;
}

/**
 * Custom hook to manage route algorithm selection based on the current route view mode.
 *
 * This hook orchestrates different algorithm behaviors depending on the mode:
 * - Dispatch: Uses selected algorithm directly (Manual mode not available)
 * - ChangeRoute: Triggers manual click handler when Manual is selected
 * - ChangeDriver: Uses selected algorithm directly (Manual mode not available)
 *
 * The parent component maintains the authoritative state for backend API calls and multi-day persistence.
 */
export function useRouteAlgorithmSelection({
  routeViewMode,
  selectedOrderingAlgorithmType,
  parentAlgorithmType,
  tabOrder,
  handleOrderingAlgorithmChange,
  onClickManual,
  setSelectedOrderingAlgorithmType,
  selectedStopDate,
  selectedShipment,
  selectedBaseTime,
  driver,
}: UseRouteAlgorithmSelectionParams) {
  const findAlgorithmIndex = (algorithmType: OrderingAlgorithmType): number => {
    return tabOrder.findIndex((type) => type === algorithmType);
  };

  useEffect(() => {
    if (!selectedOrderingAlgorithmType) {
      return;
    }

    switch (routeViewMode) {
      case RouteViewMode.Dispatch:
        handleDispatchAlgorithm();
        break;
      case RouteViewMode.ChangeRoute:
        handleChangeRouteAlgorithm();
        break;
      case RouteViewMode.ChangeDriver:
        handleChangeDriverAlgorithm();
        break;
    }
    // `driver?.id` is intentionally included: the auto-optimize handlers
    // early-return until `driver` is populated. Without it the effect never
    // re-fires once the driver arrives, so the driver's current tasks only
    // appear after a manual algorithm click. We gate on the primitive `id`
    // (rather than the object) to avoid re-firing on unrelated re-renders.
  }, [
    selectedStopDate,
    selectedOrderingAlgorithmType,
    selectedShipment,
    selectedBaseTime,
    driver?.id,
  ]);

  /**
   * Handle algorithm selection for Dispatch mode.
   * Uses the selected algorithm directly (Manual mode not available in Dispatch).
   */
  function handleDispatchAlgorithm() {
    if (!selectedOrderingAlgorithmType) return;

    const index = findAlgorithmIndex(selectedOrderingAlgorithmType);
    handleOrderingAlgorithmChange(index);
  }

  /**
   * Handle algorithm selection for ChangeRoute mode.
   * If Manual is selected, trigger the manual click handler.
   */
  function handleChangeRouteAlgorithm() {
    if (!selectedOrderingAlgorithmType) return;

    if (selectedOrderingAlgorithmType === OrderingAlgorithmType.Manual) {
      onClickManual?.();
    }
  }

  /**
   * Handle algorithm selection for ChangeDriver mode.
   * Uses the selected algorithm directly (Manual mode not available in ChangeDriver).
   */
  function handleChangeDriverAlgorithm() {
    if (!selectedOrderingAlgorithmType) return;

    const index = findAlgorithmIndex(selectedOrderingAlgorithmType);
    handleOrderingAlgorithmChange(index);
    setSelectedOrderingAlgorithmType(selectedOrderingAlgorithmType);
  }
}
